'use client';
import Checkbox from '@/components/form/input/Checkbox';
import Input from '@/components/form/input/InputField';
import Label from '@/components/form/Label';
import { useForm } from '@/components/form/useForm';
import API from '@/services/api';
import { zodResolver } from '@hookform/resolvers/zod';
import Link from 'next/link';
import React, { useState } from 'react';
import { z } from 'zod';
import Button from '../ui/button/Button';
import { Modal } from '@/components/ui/modal';

const howDidYouHearOptions = [
  'Online search',
  'Conference/Exhibition',
  'Social Media (Twitter, Linkedin, Facebook, etc.)',
  'Referral',
  'Others',
];

const publicEmailDomains = [
  'gmail.com',
  'yahoo.com',
  'hotmail.com',
  'outlook.com',
  'aol.com',
  'icloud.com',
  'mailinator.com',
  'protonmail.com',
  'zoho.com',
  'gmx.com',
  'yandex.com',
  'mail.com',
  'rediffmail.com',
  'live.com',
  'msn.com',
  'ymail.com',
  'inbox.com',
  'fastmail.com',
  '33mail.com',
];

const signUpSchema = z.object({
  firstName: z
    .string({ required_error: 'First Name is required' })
    .min(2, { message: 'First Name is required' })
    .max(50, 'First Name is too long'),
  lastName: z
    .string({ required_error: 'Last Name is required' })
    .min(2, { message: 'Last Name is required' })
    .max(50, 'Last Name is too long'),
  email: z
    .string({ required_error: 'Email is required' })
    .email({ message: 'Invalid email address' })
    .max(100, 'Email is too long')
    .refine(
      (val) => {
        const domain = val.split('@')[1]?.toLowerCase();
        return domain && !publicEmailDomains.includes(domain);
      },
      { message: 'Please use your company email address' },
    ),
  jobTitle: z
    .string({ required_error: 'Job title is required' })
    .min(2, { message: 'Job Title is required' })
    .max(100, 'Job Title is too long'),
  company: z
    .string({ required_error: 'Company is required' })
    .min(2, { message: 'Company is required' })
    .max(100, 'Company is too long'),
  countryRegion: z
    .string({ required_error: 'Country/Region is required' })
    .min(2, { message: 'Country/Region is required' }),
  howDidYouHear: z.enum(howDidYouHearOptions as [string, ...string[]], {
    message: 'Please select valid option',
  }),
  message: z.string().max(1000, 'Message is too long').optional(),
  terms: z
    .boolean({ required_error: 'Please accept terms & conditions' })
    .refine((val) => val === true, { message: 'You must accept the terms' }),
});
type SignUpFormValues = z.infer<typeof signUpSchema>;

const countryOptions = [
  'United States',
  'India',
  'United Kingdom',
  'Canada',
  'Australia',
  'Germany',
  'France',
  'Other',
];

export default function SignUpForm() {
  const [isChecked, setIsChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const form = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      jobTitle: '',
      company: '',
      countryRegion: '',
      howDidYouHear: undefined,
      message: '',
      terms: true, // must be true for z.literal(true)
    },
  });

  // Keep react-hook-form in sync with checkbox
  React.useEffect(() => {
    form.setValue('terms', isChecked);
  }, [isChecked, form]);

  // Placeholder for server action
  async function onSubmit(data: SignUpFormValues) {
    setLoading(true);
    setError(null);
    try {
      await API.post('/auth/signup', data);
      setShowSuccessModal(true);
      form.reset();
    } catch (err: unknown) {
      let message = 'Signup failed. Please try again.';
      if (typeof err === 'string') {
        message = err;
      } else if (err && typeof err === 'object') {
        if ('message' in err && typeof (err as { message?: unknown }).message === 'string') {
          message = (err as { message: string }).message;
        } else if ('error' in err && typeof (err as { error?: unknown }).error === 'string') {
          message = (err as { error: string }).error;
        }
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="no-scrollbar flex w-full flex-1 flex-col overflow-y-auto lg:w-1/2">
      {/* <div className="w-full max-w-md sm:pt-10 mx-auto mb-5">
        <Link
          href="/"
          className="inline-flex items-center text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
        >
          <ChevronLeftIcon />
          Back to dashboard
        </Link>
      </div> */}
      <div className="mx-auto my-20 flex w-full max-w-md flex-1 flex-col justify-center">
        <div>
          <div className="mb-5 sm:mb-8">
            <h1 className="text-title-sm sm:text-title-md mb-2 font-semibold text-gray-800 dark:text-white/90">
              Sign Up
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter your details to sign up!
            </p>
          </div>
          <div>
            {/* <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-5">
              <button className="inline-flex items-center justify-center gap-3 py-3 text-sm font-normal text-gray-700 transition-colors bg-gray-100 rounded-lg px-7 hover:bg-gray-200 hover:text-gray-800 dark:bg-white/5 dark:text-white/90 dark:hover:bg-white/10">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18.7511 10.1944C18.7511 9.47495 18.6915 8.94995 18.5626 8.40552H10.1797V11.6527H15.1003C15.0011 12.4597 14.4654 13.675 13.2749 14.4916L13.2582 14.6003L15.9087 16.6126L16.0924 16.6305C17.7788 15.1041 18.7511 12.8583 18.7511 10.1944Z"
                    fill="#4285F4"
                  />
                  <path
                    d="M10.1788 18.75C12.5895 18.75 14.6133 17.9722 16.0915 16.6305L13.274 14.4916C12.5201 15.0068 11.5081 15.3666 10.1788 15.3666C7.81773 15.3666 5.81379 13.8402 5.09944 11.7305L4.99473 11.7392L2.23868 13.8295L2.20264 13.9277C3.67087 16.786 6.68674 18.75 10.1788 18.75Z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.10014 11.7305C4.91165 11.186 4.80257 10.6027 4.80257 9.99992C4.80257 9.3971 4.91165 8.81379 5.09022 8.26935L5.08523 8.1534L2.29464 6.02954L2.20333 6.0721C1.5982 7.25823 1.25098 8.5902 1.25098 9.99992C1.25098 11.4096 1.5982 12.7415 2.20333 13.9277L5.10014 11.7305Z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M10.1789 4.63331C11.8554 4.63331 12.9864 5.34303 13.6312 5.93612L16.1511 3.525C14.6035 2.11528 12.5895 1.25 10.1789 1.25C6.68676 1.25 3.67088 3.21387 2.20264 6.07218L5.08953 8.26943C5.81381 6.15972 7.81776 4.63331 10.1789 4.63331Z"
                    fill="#EB4335"
                  />
                </svg>
                Sign up with Google
              </button>
              <button className="inline-flex items-center justify-center gap-3 py-3 text-sm font-normal text-gray-700 transition-colors bg-gray-100 rounded-lg px-7 hover:bg-gray-200 hover:text-gray-800 dark:bg-white/5 dark:text-white/90 dark:hover:bg-white/10">
                <svg
                  width="21"
                  className="fill-current"
                  height="20"
                  viewBox="0 0 21 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M15.6705 1.875H18.4272L12.4047 8.75833L19.4897 18.125H13.9422L9.59717 12.4442L4.62554 18.125H1.86721L8.30887 10.7625L1.51221 1.875H7.20054L11.128 7.0675L15.6705 1.875ZM14.703 16.475H16.2305L6.37054 3.43833H4.73137L14.703 16.475Z" />
                </svg>
                Sign up with X
              </button>
            </div> */}
            {/* <div className="relative py-3 sm:py-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-800"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="p-2 text-gray-400 bg-white dark:bg-gray-900 sm:px-5 sm:py-2">
                  Or
                </span>
              </div>
            </div> */}
            {/* {error && <div className="text-error-500 mb-4 text-sm">{error}</div>} */}
            {/* Success Modal */}
            <Modal
              showCloseButton={false}
              isOpen={showSuccessModal}
              onClose={() => setShowSuccessModal(false)}
            >
              <div className="mx-auto max-w-md rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-gray-900">
                <h2 className="mb-3 text-xl font-semibold text-gray-800 dark:text-white">
                  Request Received
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  We have received your request for account creation.
                  <br />
                  Once we verify your details, we will send you an email for verification.
                </p>
                <button
                  className="mt-6 rounded-lg bg-[color:var(--color-brand-primary)] px-6 py-2 text-base font-medium text-white shadow-sm transition hover:bg-[color:var(--color-brand-primary)]/90"
                  onClick={() => setShowSuccessModal(false)}
                >
                  Close
                </button>
              </div>
            </Modal>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  {/* <!-- First Name --> */}
                  <div className="sm:col-span-1">
                    <Label>
                      First Name<span className="text-error-500">*</span>
                    </Label>
                    <Input
                      {...form.getInputFields('firstName')}
                      placeholder="Enter your first name"
                    />
                  </div>
                  {/* <!-- Last Name --> */}
                  <div className="sm:col-span-1">
                    <Label>
                      Last Name<span className="text-error-500">*</span>
                    </Label>
                    <Input
                      {...form.getInputFields('lastName')}
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>
                {/* <!-- Email --> */}
                <div>
                  <Label>
                    Company Email<span className="text-error-500">*</span>
                  </Label>
                  <Input {...form.getInputFields('email')} placeholder="Enter your email" />
                </div>
                {/* <!-- Password --> */}
                {/* <div>
                  <Label>
                    Password<span className="text-error-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      {...form.getInputFields('password')}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                    />
                    <span
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute top-1/2 right-4 z-30 -translate-y-1/2 cursor-pointer"
                    >
                      {showPassword ? (
                        <EyeIcon className="fill-gray-500 dark:fill-gray-400" />
                      ) : (
                        <EyeCloseIcon className="fill-gray-500 dark:fill-gray-400" />
                      )}
                    </span>
                  </div>
                </div> */}
                {/* Country/Region */}
                <div>
                  <Label>
                    Country/Region<span className="text-error-500">*</span>
                  </Label>
                  <div className="relative">
                    <select
                      {...form.getInputFields('countryRegion')}
                      className={`h-11 w-full appearance-none rounded-lg border px-3 pr-8 font-normal text-gray-500 focus:ring-1 focus:outline-none ${form.formState.errors.countryRegion ? 'border-error-500 focus:border-error-500 focus:ring-error-500/10' : 'border-gray-200 hover:border-[color:var(--color-brand-primary)] focus:border-[color:var(--color-brand-primary)] focus:ring-[color:var(--color-brand-primary)]/10 dark:border-gray-800 dark:focus:border-[color:var(--color-brand-primary)] dark:focus:ring-[color:var(--color-brand-primary)]/10'} dark:bg-gray-900 dark:text-gray-400`}
                    >
                      <option value="" className="font-normal text-gray-400">
                        Select your country/region
                      </option>
                      {countryOptions.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                    {/* Custom arrow icon */}
                    <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-gray-400">
                      <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                        <path
                          d="M6 8l4 4 4-4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                  {form.formState.errors.countryRegion && (
                    <div className="text-error-500 mt-1 text-xs">
                      {form.formState.errors.countryRegion.message}
                    </div>
                  )}
                </div>
                {/* <!-- Job Title --> */}
                <div>
                  <Label>
                    Job Title<span className="text-error-500">*</span>
                  </Label>
                  <Input {...form.getInputFields('jobTitle')} placeholder="Enter your job title" />
                </div>
                {/* <!-- Company --> */}
                <div>
                  <Label>
                    Company<span className="text-error-500">*</span>
                  </Label>
                  <Input {...form.getInputFields('company')} placeholder="Enter your company" />
                </div>
                {/* How Did You Hear */}
                <div>
                  <Label>
                    How did you hear about us?<span className="text-error-500">*</span>
                  </Label>
                  <div className="relative">
                    <select
                      {...form.getInputFields('howDidYouHear')}
                      className={`h-11 w-full appearance-none rounded-lg border px-3 pr-8 font-normal text-gray-500 focus:ring-1 focus:outline-none ${form.formState.errors.howDidYouHear ? 'border-error-500 focus:border-error-500 focus:ring-error-500/10' : 'border-gray-200 hover:border-[color:var(--color-brand-primary)] focus:border-[color:var(--color-brand-primary)] focus:ring-[color:var(--color-brand-primary)]/10 dark:border-gray-800 dark:focus:border-[color:var(--color-brand-primary)] dark:focus:ring-[color:var(--color-brand-primary)]/10'} dark:bg-gray-900 dark:text-gray-400`}
                    >
                      <option value="" className="font-normal text-gray-400">
                        Select an option
                      </option>
                      {howDidYouHearOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {/* Custom arrow icon */}
                    <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-gray-400">
                      <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                        <path
                          d="M6 8l4 4 4-4"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </div>
                  {form.formState.errors.howDidYouHear && (
                    <div className="text-error-500 mt-1 text-xs">
                      {form.formState.errors.howDidYouHear.message}
                    </div>
                  )}
                </div>
                {/* <!-- Message --> */}
                <div>
                  <Label>Message</Label>
                  <textarea
                    {...form.getInputFields('message')}
                    placeholder="Enter a message (optional)"
                    className="min-h-[80px] w-full rounded-lg border px-3 py-2 text-gray-800 placeholder:text-gray-400 hover:border-[color:var(--color-brand-primary)] focus:border-[color:var(--color-brand-primary)] focus:ring-1 focus:ring-[color:var(--color-brand-primary)]/10 focus:outline-none dark:bg-gray-900 dark:text-white dark:placeholder:text-white/30 dark:focus:border-[color:var(--color-brand-primary)] dark:focus:ring-[color:var(--color-brand-primary)]/10"
                  />
                </div>
                {/* <!-- Checkbox --> */}
                <div className="flex items-center gap-3">
                  <Checkbox className="h-5 w-5" checked={isChecked} onChange={setIsChecked} />
                  <input type="hidden" {...form.getInputFields('terms')} />
                  <p className="inline-block font-normal text-gray-500 dark:text-gray-400">
                    By creating an account means you agree to the{' '}
                    <span className="text-gray-800 dark:text-white/90">Terms and Conditions,</span>{' '}
                    and our <span className="text-gray-800 dark:text-white">Privacy Policy</span>
                  </p>
                </div>
                {form.formState.errors.terms && (
                  <div className="text-error-500 mt-1 text-xs">
                    {form.formState.errors.terms.message}
                  </div>
                )}
                {error && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20">
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                )}
                {/* <!-- Button --> */}
                <div>
                  <Button
                    className="shadow-theme-xs flex w-full items-center justify-center rounded-lg bg-[color:var(--color-brand-primary)] px-4 py-3 text-sm font-medium text-white transition hover:bg-[color:var(--color-brand-primary)]/90 disabled:opacity-60"
                    type="submit"
                    disabled={form.formState.isSubmitting || loading}
                  >
                    {loading ? 'Signing up...' : 'Sign Up'}
                  </Button>
                </div>
              </div>
            </form>
            <div className="mt-5">
              <p className="text-center text-sm font-normal text-gray-700 sm:text-start dark:text-gray-400">
                Already have an account? {''}
                <Link
                  href="/login"
                  className="text-[color:var(--color-brand-primary)] hover:text-[color:var(--color-brand-primary)]/80"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
